import * as React from "react";
import classnames from "classnames";
import { DataLayer } from "@grandvisionhq/analytics";
import { Button } from "@grandvisionhq/common";
import { Text } from "@grandvisionhq/elements";
import { StoreFiltersModal } from "@grandvisionhq/fun-store-filters";
import { LocationSearch } from "@grandvisionhq/locations";
import { useIntl } from "@grandvisionhq/state";
import { storesSearchFilterEvent } from "../../../../data-layer/data-layer/actions.js";
import useStoreSearchWithoutInventory from "../../../../hooks/api/hooks/api/use-stores-search-without-inventory.js";
import { useTabs } from "../../../../hooks/hooks/use-tabs.js";
import { mapAppliedStoreFilters } from "../../../../utils/utils/map-applied-store-filters.js";
import NoStoresAlertContainer from "../../../NoStoresAlert/components/NoStoresAlert/NoStoresAlertContainer.js";
import StoreFinderItemContainer from "../../../StoreFinderItem/components/StoreFinderItem/StoreFinderItemContainer.js";
import StoreFinderMapContainer from "../../../StoreFinderMap/components/StoreFinderMap/StoreFinderMapContainer.js";
import StoreListTopContent from "../../../StoreListTopContent/components/StoreListTopContent/StoreListTopContent.js";
/* empty css                                 */
import { useStoreFinderContext } from "../../../../context/StoreFinder/context/StoreFinder/context.js";
const StoreGroupFinderContainerV2 = ({
  className,
  storeFilters: storeFiltersDefault,
  groupName,
  urls
}) => {
  const { getLabel, getFormattedLabel } = useIntl();
  const isInitialFilterState = React.useRef(true);
  const {
    stores,
    setStores,
    config,
    setHoveredStore,
    isPristineSearch,
    isStoresLoading,
    isStoreFiltersButtonEnabled,
    setIsStoresLoading,
    appliedStoreFilters,
    appliedStoreFilterCount,
    toggleAppliedStoreFilter,
    clearAppliedStoreFilters
  } = useStoreFinderContext();
  const mappedAppliedStoreFilters = React.useMemo(
    () => mapAppliedStoreFilters(appliedStoreFilters),
    [appliedStoreFilters]
  );
  const { googleMapsId } = config;
  const showTabs = useTabs();
  const [isModalOpen, setModalOpen] = React.useState(false);
  const closeModal = () => setModalOpen(false);
  const openModal = () => setModalOpen(true);
  const [searchStores, searchStoresResults] = useStoreSearchWithoutInventory({
    input: {
      groupName,
      filters: mappedAppliedStoreFilters
    }
  });
  const searchedStores = React.useMemo(
    () => searchStoresResults.data.results ?? stores,
    [searchStoresResults.data.results, stores]
  );
  const storeFilters = React.useMemo(
    () => searchStoresResults.data.filters ?? searchStoresResults.previousData?.availableFilters.filters ?? storeFiltersDefault ?? void 0,
    [
      searchStoresResults.data.filters,
      searchStoresResults.previousData?.availableFilters.filters,
      storeFiltersDefault
    ]
  );
  React.useEffect(() => {
    setIsStoresLoading(true);
    searchStores({
      variables: {
        input: {
          groupName,
          filters: mappedAppliedStoreFilters
        }
      },
      onCompleted: (data) => {
        setIsStoresLoading(false);
        if (data.filteredResults.results.length === 0) {
          setStores([]);
        }
      }
    });
  }, [groupName, mappedAppliedStoreFilters, searchStores, setIsStoresLoading]);
  React.useEffect(() => {
    if (searchedStores.length === 0) {
      return;
    }
    setStores(searchedStores);
    setIsStoresLoading(false);
  }, [searchedStores, setIsStoresLoading, setStores]);
  const onMouseEnterHandler = (store) => setHoveredStore(store);
  const onMouseLeaveHandler = () => setHoveredStore(null);
  const filterButtonText = appliedStoreFilterCount > 0 ? `${getLabel("stores.storeFilters.cta")} (${appliedStoreFilterCount})` : getLabel("stores.storeFilters.cta");
  React.useEffect(() => {
    if (isInitialFilterState.current) {
      isInitialFilterState.current = false;
      return;
    }
    const dataLayerEvent = storesSearchFilterEvent({
      activeFilters: mappedAppliedStoreFilters.map((filter) => filter.value),
      searchResults: searchedStores.length
    });
    DataLayer.pushEvent(dataLayerEvent);
  }, [mappedAppliedStoreFilters, searchedStores.length]);
  const shouldShowStoreFilters = storeFilters?.length && isStoreFiltersButtonEnabled;
  const showNoStoresAlert = !isPristineSearch && !isStoresLoading && stores.length === 0;
  return /* @__PURE__ */ React.createElement(
    LocationSearch,
    {
      className: classnames("store-group-finder", className),
      googleMapsId,
      showTabs,
      searchForm: /* @__PURE__ */ React.createElement("div", { className: "store-group-finder__filter-button-wrapper" }, shouldShowStoreFilters ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
        Button,
        {
          caption: filterButtonText,
          variant: "ghost",
          icon: "filter",
          onClick: openModal
        }
      ), /* @__PURE__ */ React.createElement(
        StoreFiltersModal,
        {
          isOpen: isModalOpen,
          onModalClose: closeModal,
          filters: storeFilters,
          appliedFilters: appliedStoreFilters,
          hasAppliedFilters: appliedStoreFilterCount > 0,
          toggleFilter: toggleAppliedStoreFilter,
          resultsCount: stores.length,
          isLoadingResults: isStoresLoading,
          clearFilters: clearAppliedStoreFilters
        }
      )) : null, /* @__PURE__ */ React.createElement(Text, null, getFormattedLabel("stores.page.group.storeFinder.description", {
        hideLabelKey: true,
        labelData: {
          storeCount: stores.length > 0 ? stores.length : "0"
        }
      }))),
      listTopContent: /* @__PURE__ */ React.createElement(
        StoreListTopContent,
        {
          className: "store-group-finder__list-top-content",
          title: getFormattedLabel(
            "stores.page.group.storeFinder.title",
            groupName ? {
              labelData: {
                groupName
              }
            } : void 0
          )
        }
      ),
      listInfoContent: /* @__PURE__ */ React.createElement(React.Fragment, null, showNoStoresAlert ? /* @__PURE__ */ React.createElement("div", { className: "store-group-finder__info-content" }, /* @__PURE__ */ React.createElement(NoStoresAlertContainer, null)) : null),
      loadingListItems: isStoresLoading,
      listItems: searchedStores.map((store) => /* @__PURE__ */ React.createElement(
        StoreFinderItemContainer,
        {
          loading: isStoresLoading,
          key: store.globalStoreId,
          store,
          onMouseEnter: onMouseEnterHandler,
          onMouseLeave: onMouseLeaveHandler,
          urls,
          showAttributesAsTags: true
        }
      )),
      mapContent: /* @__PURE__ */ React.createElement(
        StoreFinderMapContainer,
        {
          stores: searchedStores,
          infoWindowContentComponent: (props) => /* @__PURE__ */ React.createElement(StoreFinderItemContainer, { ...props, urls, view: "map", showAttributesAsTags: true })
        }
      )
    }
  );
};
export {
  StoreGroupFinderContainerV2 as default
};
//# sourceMappingURL=StoreGroupFinderContainerV2.js.map
